import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, Loader2, ArrowRight, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth, API_URL } from '@/contexts/AuthContext';

export function LoginView() {
    const [isLogin, setIsLogin] = useState(true);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [pseudonym, setPseudonym] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email || !password || (!isLogin && !pseudonym)) {
            setError('Please complete all required fields.');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            if (isLogin) {
                const res = await fetch(`${API_URL}/api/auth/login`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email, password }),
                });

                if (!res.ok) {
                    throw new Error('Invalid email or password. Please try again.');
                }

                const data = await res.json();
                login(data.user, data.token);
                navigate('/study');
                return;
            } else {
                const id = crypto.randomUUID();

                const res = await fetch(`${API_URL}/api/users`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ id, pseudonym, email, password_hash: password }),
                });

                if (!res.ok) {
                    const errorData = await res.json();
                    throw new Error(errorData.error || 'An account with this email/pseudonym already exists.');
                }

                const data = await res.json();
                login(data.user, data.token);
                navigate('/study');
                return;
            }
        } catch (err: any) {
            console.error('Authentication error:', err);
            setError(err.message || 'Failed to authenticate. Please check your connection and try again.');
        } finally {
            setIsLoading(false);
        }
    };


    return (
        <div className="min-h-screen app-background flex flex-col items-center justify-center p-6 sm:p-12 font-serif selection:bg-novel-highlight/20">

            {/* Decorative background elements inspired by the Onboarding flow */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-novel-accent/5 rounded-full blur-3xl opacity-50 mix-blend-multiply" />
                <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-novel-highlight/5 rounded-full blur-3xl opacity-50 mix-blend-multiply" />
            </div>

            <div className="w-full max-w-5xl grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch relative z-10">
                <aside className="hidden lg:flex flex-col justify-between rounded-2xl border border-zinc-200/60 dark:border-zinc-800/60 bg-white/40 dark:bg-zinc-900/40 backdrop-blur-xl p-8">
                    <div className="space-y-6">
                        <h2 className="text-4xl font-bold text-zinc-900 dark:text-zinc-50 leading-tight">
                            Built for deep writing, not dopamine loops.
                        </h2>
                        <p className="text-zinc-600 dark:text-zinc-300 leading-relaxed font-sans">
                            Draft in the Study, plan in Constellation, and publish to the Coffee House. Everything is designed to keep your momentum.
                        </p>
                        <ul className="space-y-3 text-sm font-sans text-zinc-700 dark:text-zinc-300">
                            <li>1. Manuscript + planning canvas in one workspace</li>
                            <li>2. Public reader experience with direct author support</li>
                            <li>3. Cloud sync, export, and version history</li>
                        </ul>
                    </div>
                    <Link to="/read" className="inline-flex items-center gap-2 text-sm font-semibold text-novel-accent hover:underline">
                        Preview the live Coffee House <ArrowRight className="w-4 h-4" />
                    </Link>
                </aside>

                <div className="w-full max-w-md mx-auto space-y-8 p-8 sm:p-10 rounded-2xl bg-white/50 dark:bg-zinc-900/50 backdrop-blur-xl border border-zinc-200/50 dark:border-zinc-800/50 shadow-2xl">

                <div className="text-center space-y-2">
                    <h1 className="text-4xl font-bold tracking-tight text-zinc-900 dark:text-zinc-50">
                        Write Library
                    </h1>
                    <p className="text-lg text-zinc-500 dark:text-zinc-400 font-sans">
                        {isLogin ? 'Enter your Study.' : 'Your stories deserve a home.'}
                    </p>
                </div>

                {error && (
                    <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm font-sans rounded-lg border border-red-200 dark:border-red-900/50">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6 font-sans">
                    <div className="space-y-4">
                        {!isLogin && (
                            <div className="space-y-2">
                                <Label htmlFor="pseudonym">Pseudonym</Label>
                                <div className="relative">
                                    <User className="absolute left-3 top-2.5 h-5 w-5 text-zinc-400" />
                                    <Input
                                        id="pseudonym"
                                        type="text"
                                        placeholder="Your pen name"
                                        className="pl-10 pb-2 pt-2 bg-white/50 dark:bg-zinc-950/50 focus:bg-white dark:focus:bg-zinc-950 transition-colors"
                                        value={pseudonym}
                                        onChange={(e) => setPseudonym(e.target.value)}
                                        disabled={isLoading}
                                        required={!isLogin}
                                        maxLength={15}
                                    />
                                </div>
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label htmlFor="email">Email address</Label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-2.5 h-5 w-5 text-zinc-400" />
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="author@example.com"
                                    className="pl-10 pb-2 pt-2 bg-white/50 dark:bg-zinc-950/50 focus:bg-white dark:focus:bg-zinc-950 transition-colors"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={isLoading}
                                    autoComplete="email"
                                    required
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <Label htmlFor="password">Password</Label>
                                {isLogin && (
                                    <Link to="/forgot-password" className="text-sm text-novel-accent hover:underline font-sans">
                                        Forgot Password?
                                    </Link>
                                )}
                            </div>
                            <div className="relative">
                                <Lock className="absolute left-3 top-2.5 h-5 w-5 text-zinc-400" />
                                <Input
                                    id="password"
                                    type="password"
                                    placeholder="••••••••"
                                    className="pl-10 pb-2 pt-2 bg-white/50 dark:bg-zinc-950/50 focus:bg-white dark:focus:bg-zinc-950 transition-colors"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    disabled={isLoading}
                                    autoComplete={isLogin ? "current-password" : "new-password"}
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    <Button
                        type="submit"
                        className="w-full text-base h-11 bg-zinc-900 hover:bg-zinc-800 text-white dark:bg-zinc-100 dark:hover:bg-white dark:text-zinc-900 group"
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <Loader2 className="mr-2 h-5 w-5 animation-spin" />
                        ) : (
                            <>
                                {isLogin ? 'Get into the Write Library' : 'Join the Write Library'}
                                <ArrowRight className="ml-2 h-4 w-4 opacity-70 group-hover:translate-x-1 transition-transform" />
                            </>
                        )}
                    </Button>
                </form>

                <div className="text-center font-sans tracking-wide">
                    <button
                        type="button"
                        onClick={() => {
                            setIsLogin(!isLogin);
                            setError(null);
                        }}
                        className="text-sm text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100 transition-colors"
                        disabled={isLoading}
                    >
                        {isLogin
                            ? "Don't have an account? Sign up"
                            : "Already have an account? Log in"}
                    </button>
                </div>

                </div>
            </div>

            {/* Footer Trust */}
            <div className="absolute bottom-8 text-center text-sm text-zinc-400 dark:text-zinc-600 font-sans max-w-sm px-6">
                <p>Your work syncs through the Cloudflare-hosted API with JWT auth.</p>
                <Link to="/read" className="block mt-4 opacity-70 hover:opacity-100 hover:text-novel-accent transition-all">
                    Just want to read? Enter The Coffee House &rarr;
                </Link>
            </div>

        </div >
    );
}
